import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogoutButton } from "../Utility/AuthTokenRetrieve";
import "../Food/RecipeList.css";
import "./Scrapbook.css";
import {
  deleteScrapbookEntry,
  deleteScrapbookImage,
  emptyScrapbookForm,
  fetchScrapbookEntries,
  scrapbookCategories,
  saveScrapbookEntry,
} from "./ScrapbookShared";

const trackingIdeas = [
  "Weight, length, and head circumference",
  "Clothing size and diaper size",
  "First smile, laugh, roll, crawl, step, word, tooth, and haircut",
  "Favorite toy, book, song, sound, food, and nickname",
  "Doctor visits, vaccines, health notes, and recovery milestones",
  "Family visits, holidays, trips, daycare, and monthly photos",
];

const ScrapbookAdmin = () => {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(emptyScrapbookForm);
  const [files, setFiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const refreshEntries = () => {
    fetchScrapbookEntries()
      .then((data) => setEntries(data.entries || []))
      .catch(() => setStatusMessage("Unable to load scrapbook entries."));
  };

  useEffect(() => {
    refreshEntries();
  }, []);

  const updateForm = (field, value) => {
    setForm((currentForm) => ({ ...currentForm, [field]: value }));
  };

  const resetForm = () => {
    setForm(emptyScrapbookForm);
    setFiles([]);
    setEditingId(null);
    setShowForm(false);
  };

  const editEntry = (entry) => {
    setForm({
      title: entry.title || "",
      entryDate: entry.entryDate || "",
      ageLabel: entry.ageLabel || "",
      category: entry.category || "Milestone",
      story: entry.story || "",
      weightValue: entry.weightValue || "",
      weightUnit: entry.weightUnit || "lb",
      lengthValue: entry.lengthValue || "",
      lengthUnit: entry.lengthUnit || "in",
      headCircumferenceValue: entry.headCircumferenceValue || "",
      headCircumferenceUnit: entry.headCircumferenceUnit || "in",
      clothingSize: entry.clothingSize || "",
      diaperSize: entry.diaperSize || "",
      favoriteToy: entry.favoriteToy || "",
      favoriteSong: entry.favoriteSong || "",
      newWords: entry.newWords || "",
      newFoods: entry.newFoods || "",
      healthNotes: entry.healthNotes || "",
      tags: entry.tagsText || "",
      isFeatured: !!entry.isFeatured,
    });
    setFiles([]);
    setEditingId(entry.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitForm = (event) => {
    event.preventDefault();
    setIsSaving(true);
    setStatusMessage("");
    saveScrapbookEntry(form, files, editingId)
      .then((data) => {
        if (data.error) {
          setStatusMessage(data.error);
          return;
        }
        setStatusMessage(data.warning || (editingId ? "Scrapbook entry updated." : "Scrapbook entry added."));
        resetForm();
        refreshEntries();
      })
      .catch((error) => setStatusMessage(error.message || "Unable to save scrapbook entry."))
      .finally(() => setIsSaving(false));
  };

  const removeEntry = (entryId) => {
    deleteScrapbookEntry(entryId)
      .then(() => {
        setStatusMessage("Scrapbook entry deleted.");
        refreshEntries();
      })
      .catch((error) => setStatusMessage(error.message || "Unable to delete scrapbook entry."));
  };

  const removeImage = (imageId) => {
    deleteScrapbookImage(imageId)
      .then(() => {
        setStatusMessage("Image deleted.");
        refreshEntries();
      })
      .catch((error) => setStatusMessage(error.message || "Unable to delete image."));
  };

  return (
    <div className="ScrapbookPage">
      <div className="searchBarDiv formViewWrapper ScrapbookIntro">
        <div>
          <h2 className="ScrapbookTitle">Jon Scrapbook Admin</h2>
          <p className="ScrapbookSubtitle">
            Add timeline moments, growth details, and private S3-hosted photos.
          </p>
        </div>
        <div className="ScrapbookTopActions">
          <Link className="summaryButton ScrapbookAdminLink" to="/scrapbook">Timeline</Link>
          <LogoutButton />
        </div>
      </div>

      <div className="ScrapbookAdminGrid">
        <div className="shoppingListDiv ScrapbookPanel">
          <div className="ScrapbookSectionHeader">
            <h3>Entry Editor</h3>
            <button className="summaryButton" type="button" onClick={() => setShowForm(!showForm)}>
              {showForm ? "Hide Form" : "Add Entry"}
            </button>
          </div>
          {statusMessage ? <p className="ScrapbookStatus">{statusMessage}</p> : null}

          {showForm ? (
            <form onSubmit={submitForm}>
              <div className="ScrapbookFormGrid">
                <div className="inputDiv">
                  <label>Title</label>
                  <input className="inputBox addInput" value={form.title} onChange={(event) => updateForm("title", event.target.value)} required />
                </div>
                <div className="inputDiv">
                  <label>Date</label>
                  <input className="inputBox addInput" type="date" value={form.entryDate} onChange={(event) => updateForm("entryDate", event.target.value)} required />
                </div>
                <div className="inputDiv">
                  <label>Manual age label</label>
                  <input className="inputBox addInput" placeholder="2 weeks, 1 month" value={form.ageLabel} onChange={(event) => updateForm("ageLabel", event.target.value)} />
                </div>
                <div className="inputDiv">
                  <label>Category</label>
                  <select className="inputBox addInput" value={form.category} onChange={(event) => updateForm("category", event.target.value)}>
                    {scrapbookCategories.map((category) => <option key={category}>{category}</option>)}
                  </select>
                </div>
              </div>

              <div className="inputDiv">
                <label>Story</label>
                <textarea className="inputBox textAreaAdd ScrapbookTextarea" value={form.story} onChange={(event) => updateForm("story", event.target.value)} />
              </div>

              <div className="ScrapbookFormGrid">
                <div className="inputDiv">
                  <label>Weight</label>
                  <input className="inputBox addInput" type="number" step="0.01" value={form.weightValue} onChange={(event) => updateForm("weightValue", event.target.value)} />
                </div>
                <div className="inputDiv">
                  <label>Weight unit</label>
                  <input className="inputBox addInput" value={form.weightUnit} onChange={(event) => updateForm("weightUnit", event.target.value)} />
                </div>
                <div className="inputDiv">
                  <label>Length</label>
                  <input className="inputBox addInput" type="number" step="0.01" value={form.lengthValue} onChange={(event) => updateForm("lengthValue", event.target.value)} />
                </div>
                <div className="inputDiv">
                  <label>Length unit</label>
                  <input className="inputBox addInput" value={form.lengthUnit} onChange={(event) => updateForm("lengthUnit", event.target.value)} />
                </div>
                <div className="inputDiv">
                  <label>Head circumference</label>
                  <input className="inputBox addInput" type="number" step="0.01" value={form.headCircumferenceValue} onChange={(event) => updateForm("headCircumferenceValue", event.target.value)} />
                </div>
                <div className="inputDiv">
                  <label>Head unit</label>
                  <input className="inputBox addInput" value={form.headCircumferenceUnit} onChange={(event) => updateForm("headCircumferenceUnit", event.target.value)} />
                </div>
              </div>

              <div className="ScrapbookFormGrid">
                <div className="inputDiv">
                  <label>Clothing size</label>
                  <input className="inputBox addInput" value={form.clothingSize} onChange={(event) => updateForm("clothingSize", event.target.value)} />
                </div>
                <div className="inputDiv">
                  <label>Diaper size</label>
                  <input className="inputBox addInput" value={form.diaperSize} onChange={(event) => updateForm("diaperSize", event.target.value)} />
                </div>
                <div className="inputDiv">
                  <label>Favorite toy</label>
                  <input className="inputBox addInput" value={form.favoriteToy} onChange={(event) => updateForm("favoriteToy", event.target.value)} />
                </div>
                <div className="inputDiv">
                  <label>Favorite song</label>
                  <input className="inputBox addInput" value={form.favoriteSong} onChange={(event) => updateForm("favoriteSong", event.target.value)} />
                </div>
                <div className="inputDiv">
                  <label>New words or sounds</label>
                  <input className="inputBox addInput" value={form.newWords} onChange={(event) => updateForm("newWords", event.target.value)} />
                </div>
                <div className="inputDiv">
                  <label>New foods</label>
                  <input className="inputBox addInput" value={form.newFoods} onChange={(event) => updateForm("newFoods", event.target.value)} />
                </div>
              </div>

              <div className="inputDiv">
                <label>Health notes</label>
                <textarea className="inputBox textAreaAdd ScrapbookTextarea" value={form.healthNotes} onChange={(event) => updateForm("healthNotes", event.target.value)} />
              </div>
              <div className="inputDiv">
                <label>Tags</label>
                <input className="inputBox addInput" placeholder="smiles, grandma, tummy time" value={form.tags} onChange={(event) => updateForm("tags", event.target.value)} />
              </div>
              <div className="inputDiv">
                <label>Photos</label>
                <input className="inputBox addInput" type="file" accept="image/*" multiple onChange={(event) => setFiles(event.target.files)} />
              </div>
              <label className="ScrapbookCheckbox">
                <input type="checkbox" checked={form.isFeatured} onChange={(event) => updateForm("isFeatured", event.target.checked)} />
                Feature this entry at the top of the scrapbook
              </label>
              <div className="BabyTrackerActionRow">
                <button className="summaryButton" type="submit" disabled={isSaving}>{isSaving ? "Saving..." : "Save Entry"}</button>
                <button className="summaryButton" type="button" onClick={resetForm}>Cancel</button>
              </div>
            </form>
          ) : null}
        </div>

        <div className="shoppingListDiv ScrapbookPanel">
          <h3>Tracking Ideas</h3>
          <div className="ScrapbookIdeaList">
            {trackingIdeas.map((idea) => <span key={idea}>{idea}</span>)}
          </div>
        </div>
      </div>

      <div className="shoppingListDiv ScrapbookPanel">
        <h3>Existing Entries</h3>
        <div className="ScrapbookAdminEntryList">
          {entries.length ? entries.map((entry) => (
            <article className="ScrapbookAdminEntry" key={entry.id}>
              <div>
                <strong>{entry.title}</strong>
                <span>{entry.entryDateDisplay} {entry.ageLabel ? `- ${entry.ageLabel}` : ""}</span>
              </div>
              <div className="ScrapbookImageAdminRow">
                {entry.images?.map((image) => (
                  <div className="ScrapbookImageAdmin" key={image.id}>
                    <img src={image.url} alt={image.originalFilename || entry.title} />
                    <button className="summaryButton" type="button" onClick={() => removeImage(image.id)}>Delete Image</button>
                  </div>
                ))}
              </div>
              <div className="BabyTrackerActionRow">
                <button className="summaryButton" type="button" onClick={() => editEntry(entry)}>Edit</button>
                <button className="summaryButton" type="button" onClick={() => removeEntry(entry.id)}>Delete</button>
              </div>
            </article>
          )) : <p>No scrapbook entries yet.</p>}
        </div>
      </div>
    </div>
  );
};

export default ScrapbookAdmin;
